import { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Heading,
  Text,
  IconButton,
  HStack,
  Spacer,
  Menu,
  MenuButton,
  MenuList,
  Button,
  MenuItem,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";

import Activities from "../chat/Activities"; // adjust the path if needed
import Translate from "../../components/LanguageTranslation/Translate";
import FilterMenu from "../common/FilterMenu";
import { EXPLORE_SUBS } from "../BucketList/bucketConstants";
import React from "react";

export default function ItineraryTabs({
  generalInfo,
  cityItinerary = [],
  selectedDayIndex,
  setSelectedDayIndex,
  tripId,
  cityName,
  onActivitiesChange,
  handleUpdateClickInternal,
  handleSuggestionClick,
  city,
  setIsAddActivitiesOpen,
  cityTripPlan,
  fromItineray,
  fromChatItineray,
  handleDayChange,
  selectedCityIndex = 0,
  setSelectedCityIndex = () => {},
}) {
  const tabListRef = useRef(null);
  const [activityFilter, setActivityFilter] = useState(["all"]);

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Collect all days from all cities
  const getAllDays = () => {
    if (!generalInfo?.destinations) return [];

    const allDays = [];
    let globalDayIndex = 0;
    let continuousDayNumber = 1; // Start from day 1 and increment continuously

    generalInfo.destinations.forEach((destination, cityIndex) => {
      const cityItinerary = destination.trip_plan?.itinerary || [];
      cityItinerary.forEach((day, dayIndex) => {
        // Calculate the display day number based on the original day value
        // For first city, keep original values (0, 1, 2, etc.)
        // For subsequent cities, add the total days from previous cities
        let displayDayNumber;
        if (cityIndex === 0) {
          // First city: keep original day values (0, 1, 2, etc.)
          displayDayNumber = parseInt(day.day);
        } else {
          // Subsequent cities: calculate the last day number of the first destination
          let lastDayOfFirstDestination = 0;
          const firstDestination = generalInfo.destinations[0];
          if (firstDestination?.trip_plan?.itinerary?.length > 0) {
            // Get the last day number of the first destination
            const lastDayOfFirstCity = parseInt(
              firstDestination.trip_plan.itinerary[
                firstDestination.trip_plan.itinerary.length - 1
              ].day
            );
            lastDayOfFirstDestination = lastDayOfFirstCity;
          }
          // Start from the last day of first destination + 1
          // So if Amsterdam ends at day 5, Rotterdam starts at day 6
          // If Amsterdam ends at day 0, Rotterdam starts at day 1
          // If Amsterdam ends at day 1, Rotterdam starts at day 2
          // The formula should be: lastDayOfFirstDestination + currentDayValue
          displayDayNumber = lastDayOfFirstDestination + parseInt(day.day);
        }

        allDays.push({
          ...day,
          cityIndex,
          cityName: destination.city || `City ${cityIndex + 1}`,
          originalDayIndex: dayIndex,
          globalDayIndex,
          continuousDayNumber: displayDayNumber, // Use calculated display day number
          day: displayDayNumber, // Override the day value to be continuous
        });
        globalDayIndex++;
      });
    });

    // Debug log to understand the structure
    // console.log('All days collected:', allDays);

    return allDays;
  };

  const allDays = getAllDays();

  // Find the global day index for the currently selected day
  const getGlobalDayIndex = () => {
    if (!allDays.length) return 0;

    // Find the day that matches the current city and day selection
    const globalIndex = allDays.findIndex(
      (day) =>
        day.cityIndex === selectedCityIndex &&
        day.originalDayIndex === selectedDayIndex
    );

    return globalIndex >= 0 ? globalIndex : 0;
  };

  const globalDayIndex = getGlobalDayIndex();

  // Filter activities based on selected filter
  const getFilteredActivities = (activities) => {
    if (!Array.isArray(activities)) {
      return [];
    }

    if (activityFilter.includes("all")) {
      return activities;
    }

    if (activityFilter.includes("none")) {
      return [];
    }

    return activities.filter(
      (activity) => activity.type && activityFilter.includes(activity.type)
    );
  };

  const checkScrollButtons = () => {
    if (tabListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabListRef.current;
      setShowLeftArrow(scrollLeft > 1);
      setShowRightArrow(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
    }
  };

  useEffect(() => {
    // Initial checks with multiple retries
    const initialCheck = () => {
      checkScrollButtons();
      // Retry after short delays to ensure content is fully loaded
      setTimeout(checkScrollButtons, 100);
      setTimeout(checkScrollButtons, 500);
      setTimeout(checkScrollButtons, 1000);
    };

    // Run initial check
    initialCheck();

    // Add event listeners
    const tabListElement = tabListRef.current;
    const handleResize = () => {
      requestAnimationFrame(checkScrollButtons);
    };

    tabListElement?.addEventListener("scroll", checkScrollButtons);
    window.addEventListener("resize", handleResize);
    window.addEventListener("load", initialCheck);

    // Observe changes in the tabList content
    const observer = new MutationObserver(checkScrollButtons);
    if (tabListElement) {
      observer.observe(tabListElement, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    // Cleanup
    return () => {
      tabListElement?.removeEventListener("scroll", checkScrollButtons);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("load", initialCheck);
      observer.disconnect();
    };
  }, [allDays, selectedDayIndex]);

  // Scroll to selected tab when city changes
  useEffect(() => {
    if (tabListRef.current && allDays.length > 0) {
      // Find the first day of the selected city
      const firstDayOfSelectedCity = allDays.findIndex(
        (day) => day.cityIndex === selectedCityIndex
      );

      if (firstDayOfSelectedCity >= 0) {
        // Get the tab element
        const tabElements = tabListRef.current.querySelectorAll('[role="tab"]');
        const targetTab = tabElements[firstDayOfSelectedCity];

        if (targetTab) {
          // Calculate scroll position to center the tab
          const containerWidth = tabListRef.current.clientWidth;
          const tabLeft = targetTab.offsetLeft;
          const tabWidth = targetTab.offsetWidth;
          const scrollLeft = tabLeft - containerWidth / 2 + tabWidth / 2;

          // Smooth scroll to the tab with a small delay to ensure DOM is ready
          setTimeout(() => {
            if (tabListRef.current) {
              tabListRef.current.scrollTo({
                left: Math.max(0, scrollLeft),
                behavior: "smooth",
              });
            }
          }, 100);
        }
      }
    }
  }, [selectedCityIndex]); // Removed allDays dependency to prevent interference

  const tabListscrollLeft = () => {
    if (tabListRef.current) {
      tabListRef.current.scrollBy({
        left: -300, // Adjust this value to control the scroll distance
        behavior: "smooth", // Smooth scrolling
      });
      // Update scroll button visibility after scrolling
      setTimeout(() => checkScrollButtons(), 300);
    }
  };

  const tabListscrollRight = () => {
    if (tabListRef.current) {
      tabListRef.current.scrollBy({
        left: 300, // Adjust this value to control the scroll distance
        behavior: "smooth", // Smooth scrolling
      });
      // Update scroll button visibility after scrolling
      setTimeout(() => checkScrollButtons(), 300);
    }
  };

  // Handle day selection - switch to the appropriate city
  const handleDaySelection = (index) => {
    const selectedDay = allDays[index];
    // console.log('Day selected:', { index, selectedDay, currentCityIndex: selectedCityIndex });

    if (selectedDay) {
      if (selectedDay.cityIndex !== selectedCityIndex && setSelectedCityIndex) {
        // Switch to the city that contains this day
        // console.log('Switching to city:', selectedDay.cityIndex);
        setSelectedCityIndex(selectedDay.cityIndex);
        // Set the day index within that city
        setSelectedDayIndex(selectedDay.originalDayIndex);
      } else {
        // Same city, just update the day index
        setSelectedDayIndex(selectedDay.originalDayIndex);
      }

      if (handleDayChange) {
        handleDayChange(selectedDay.originalDayIndex);
      }
    }
  };

  if (allDays.length === 0) return null;

  return (
    <Box>
      <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
        <Translate text="Itinerary" />
      </Text>
      <Tabs
        variant="unstyled"
        onChange={handleDaySelection}
        index={globalDayIndex}
      >
        <Box position="relative" w="100%">
          {showLeftArrow && (
            <IconButton
              aria-label="Scroll Left"
              icon={<ChevronLeftIcon />}
              onClick={tabListscrollLeft}
              isRound={true}
              size="xs"
              position="absolute"
              top="40%"
              left="0px"
              transform="translateY(-50%)"
              backgroundColor="white"
              boxShadow="base"
              zIndex="overlay"
            />
          )}
          <TabList
            ref={tabListRef}
            overflowX="auto"
            position="relative"
            sx={{
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            {allDays.map((day, index) => {
              const isCurrentCity = day.cityIndex === selectedCityIndex;
              const isSelectedDay = index === globalDayIndex;

              return (
                <Tab
                  key={index}
                  _selected={{
                    bg: "white",
                    border: "2px solid black",
                    opacity: 1,
                  }}
                  _hover={{ bg: "none" }}
                  p={2}
                  pl={3}
                  pr={3}
                  border="2px solid"
                  borderColor={isCurrentCity ? "gray.200" : "gray.100"}
                  borderRadius="xl"
                  mr={3}
                  mb={4}
                  align="left"
                  minW="15%"
                  maxW="20%"
                  textAlign="center"
                  opacity={isCurrentCity ? 1 : 0.7}
                  transition="all 0.2s"
                >
                  <VStack alignItems="flex-start" justifyContent="flex-start">
                    <Heading
                      fontSize="md"
                      fontWeight="bold"
                      mb={0}
                      fontFamily="Arial"
                    >
                      <Translate text="Day" /> {day.day}
                    </Heading>
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      mb={0}
                      fontFamily="Arial"
                      noOfLines={1}
                      textAlign="left"
                    >
                      <Translate
                        text={
                          !isCurrentCity
                            ? day.cityName
                            : day?.location || "No Location Available"
                        }
                      />
                    </Text>
                  </VStack>
                </Tab>
              );
            })}
          </TabList>
          {/* Right Scroll Button */}
          {showRightArrow && (
            <IconButton
              aria-label="Scroll Right"
              icon={<ChevronRightIcon />}
              onClick={tabListscrollRight}
              isRound={true}
              size="xs"
              position="absolute"
              top="40%"
              right="0px"
              transform="translateY(-50%)"
              backgroundColor="white"
              boxShadow="base"
              zIndex="overlay"
            />
          )}
        </Box>
        <TabPanels>
          {allDays.map((day, index) => {
            const isCurrentCity = day.cityIndex === selectedCityIndex;

            return (
              <TabPanel
                key={index}
                p={5}
                border="2px solid"
                borderColor={isCurrentCity ? "gray.200" : "gray.100"}
                borderRadius="xl"
                opacity={isCurrentCity ? 1 : 0.7}
                transition="all 0.2s"
              >
                <Heading fontSize="18px" fontFamily="Arial" mb={0}>
                  <Translate text={day.current_day_name} />
                </Heading>
                <VStack
                  mt={5}
                  spacing={2}
                  alignItems="flex-start"
                  justifyContent="flex-start"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    fontFamily="Arial"
                    mb={0}
                  >
                    <Translate text="Morning:" />
                  </Text>
                  <Text fontSize="sm" fontFamily="Arial" mb={0}>
                    <Translate text={day.plan.morning || "No plan available"} />
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    fontFamily="Arial"
                    mb={0}
                  ></Text>
                  <Text fontSize="sm" fontFamily="Arial" mb={0}>
                    <Translate
                      text={day.plan.afternoon || "No plan available"}
                    />
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    fontFamily="Arial"
                    mb={0}
                  >
                    <Translate text="Evening:" />
                  </Text>
                  <Text fontSize="sm" fontFamily="Arial" mb={0}>
                    <Translate text={day.plan.evening || "No plan available"} />
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    fontFamily="Arial"
                    mb={0}
                  >
                    <Translate text="Bedtime:" />
                  </Text>
                  <Text fontSize="sm" fontFamily="Arial" mb={0}>
                    <Translate text={day.plan.bedtime || "No plan available"} />
                  </Text>
                </VStack>
                <VStack mt={5}>
                  <HStack spacing={0} w="full">
                    <HStack spacing={2}>
                      <Heading
                        mb={0}
                        fontSize={{ base: "sm", md: "md", lg: "lg" }}
                        textAlign="left"
                      >
                        <Translate text="Activities" />
                      </Heading>
                      {Array.isArray(day.activity_detail_list) &&
                        day.activity_detail_list.length > 0 && (
                          <FilterMenu
                            filterOptions={EXPLORE_SUBS}
                            selectedFilter={activityFilter}
                            onFilterChange={setActivityFilter}
                            multiple={true}
                            iconSize="sm"
                          />
                        )}
                    </HStack>

                    <Spacer />
                    {fromItineray && isCurrentCity && (
                      <Menu>
                        <MenuButton
                          as={Button}
                          aria-label="Update activities"
                          bg="#1A202C"
                          color="white"
                          _hover="none"
                          _active="none"
                          size={{ base: "xs", md: "xs", lg: "sm" }}
                          fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                          rightIcon={<ChevronDownIcon />}
                          onClick={handleUpdateClickInternal}
                        >
                          <Translate text="Update Activities" />
                        </MenuButton>
                        <MenuList>
                          <MenuItem
                            onClick={() => setIsAddActivitiesOpen(true)}
                            fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                          >
                            <Translate text="Add Bucket List Items" />
                          </MenuItem>
                          <MenuItem
                            onClick={() =>
                              handleSuggestionClick(
                                `Could you give me more recommendation for activities in ${day.cityName}`,
                                "explore_conversation"
                              )
                            }
                            fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                          >
                            <Translate text="Get more Recommendations" />
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    )}
                  </HStack>

                  <Activities
                    data={getFilteredActivities(day.activity_detail_list || [])}
                    conversationId={tripId}
                    cityName={day.cityName}
                    onActivitiesChange={onActivitiesChange}
                    tripPlan={cityTripPlan}
                    fromItineray={fromItineray}
                    fromChatItineray={fromChatItineray}
                    itineraryTabs={true}
                    dayIndex={day.originalDayIndex}
                  />
                </VStack>
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
    </Box>
  );
}
